import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Link, Paper, Table,
  TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import { portalErrorMessage } from '../portalHttp';
import { usePortalList } from '../usePortalList';
import { saveBlob } from '../../utils/saveBlob';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { SupplierPoDetail, SupplierPoRow, SupplierPortalApi } from './supplierPortalApi';
import { useSupplierSession } from './SupplierPortalShell';

// ---- F8 supplier portal: purchase orders (own supplier only; scoping is server-side) ----
const SupplierPos: React.FC = () => {
  const { code } = useSupplierSession();
  const { page, setPage, size, setSize, data, error, loading } = usePortalList<SupplierPoRow>((p, s) => SupplierPortalApi.pos(p, s), []);
  const base = `/portal/${encodeURIComponent(code)}/supplier`;
  return (
    <Box>
      <Typography variant="h5" gutterBottom>Purchase orders</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && !data ? <CircularProgress /> : (
        <Paper><TableContainer><Table size="small">
          <TableHead><TableRow>
            <TableCell>PO</TableCell><TableCell>Order date</TableCell><TableCell>Expected</TableCell>
            <TableCell align="right">Total</TableCell><TableCell>Status</TableCell><TableCell>Acknowledged</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {data?.rows.length === 0 && <TableRow><TableCell colSpan={6} align="center">No purchase orders yet.</TableCell></TableRow>}
            {data?.rows.map(r => (
              <TableRow key={r.id} hover>
                <TableCell><Link component={RouterLink} to={`${base}/pos/${r.id}`}>{r.poNumber}</Link></TableCell>
                <TableCell>{formatDate(r.orderDate)}</TableCell>
                <TableCell>{r.expectedDate ? formatDate(r.expectedDate) : ''}</TableCell>
                <TableCell align="right">{formatMoney(r.totalAmount, r.currency)}</TableCell>
                <TableCell><Chip size="small" label={humanizeEnum(r.status)} /></TableCell>
                <TableCell>{r.acknowledgedAt ? formatDateTime(r.acknowledgedAt) : 'Not yet'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table></TableContainer>
          <TablePagination component="div" count={data?.total ?? 0} page={page} rowsPerPage={size}
            rowsPerPageOptions={[10, 20, 50]} onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={e => { setSize(parseInt(e.target.value, 10)); setPage(0); }} />
        </Paper>
      )}
    </Box>
  );
};

export const SupplierPoDetailPage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const { code } = useSupplierSession();
  const [po, setPo] = useState<SupplierPoDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ackOpen, setAckOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setPo(await SupplierPortalApi.po(id)); setError(null); }
    catch (e) { setError(portalErrorMessage(e, 'Could not load this purchase order')); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const acknowledge = async () => {
    setBusy(true);
    try { setPo(await SupplierPortalApi.acknowledge(id, note.trim())); setAckOpen(false); setNote(''); }
    catch (e) { setError(portalErrorMessage(e, 'Could not acknowledge this purchase order')); }
    finally { setBusy(false); }
  };
  const download = async () => {
    if (!po) return;
    try { saveBlob(await SupplierPortalApi.poPdf(id), `${po.poNumber}.pdf`); }
    catch (e) { setError(portalErrorMessage(e, 'Could not download the PDF')); }
  };

  const back = <Link component={RouterLink} to={`/portal/${encodeURIComponent(code)}/supplier`}>Back to purchase orders</Link>;
  if (error && !po) return <Box>{back}<Alert severity="error" sx={{ mt: 2 }}>{error}</Alert></Box>;
  if (!po) return <CircularProgress />;
  return (
    <Box>
      {back}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', my: 2 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>{po.poNumber}</Typography>
        <Chip label={humanizeEnum(po.status)} />
        <Button variant="outlined" onClick={download}>Download PDF</Button>
        {po.canAcknowledge && <Button variant="contained" onClick={() => setAckOpen(true)}>Acknowledge</Button>}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {po.acknowledgedAt && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Acknowledged on {formatDateTime(po.acknowledgedAt)}{po.acknowledgedNote ? `: "${po.acknowledgedNote}"` : ''}
        </Alert>
      )}
      <Typography variant="body2" sx={{ mb: 2 }}>
        Ordered {formatDate(po.orderDate)}{po.expectedDate ? ` - expected ${formatDate(po.expectedDate)}` : ''}
      </Typography>
      <Paper><TableContainer><Table size="small">
        <TableHead><TableRow>
          <TableCell>Item</TableCell><TableCell align="right">Quantity</TableCell><TableCell align="right">Unit price</TableCell>
          <TableCell align="right">Line total</TableCell><TableCell>Delivery</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {po.lines.map((l, i) => (
            <TableRow key={i}>
              <TableCell>{l.itemCode} - {l.itemName}</TableCell>
              <TableCell align="right">{l.quantity}</TableCell>
              <TableCell align="right">{formatMoney(l.unitPrice, po.currency)}</TableCell>
              <TableCell align="right">{formatMoney(l.lineTotal, po.currency)}</TableCell>
              <TableCell>{l.deliveryDate ? formatDate(l.deliveryDate) : ''}</TableCell>
            </TableRow>
          ))}
          <TableRow><TableCell colSpan={3} align="right">Subtotal</TableCell><TableCell align="right">{formatMoney(po.subtotal, po.currency)}</TableCell><TableCell /></TableRow>
          <TableRow><TableCell colSpan={3} align="right">Tax</TableCell><TableCell align="right">{formatMoney(po.taxAmount, po.currency)}</TableCell><TableCell /></TableRow>
          <TableRow><TableCell colSpan={3} align="right"><b>Total</b></TableCell><TableCell align="right"><b>{formatMoney(po.totalAmount, po.currency)}</b></TableCell><TableCell /></TableRow>
        </TableBody>
      </Table></TableContainer></Paper>
      <Dialog open={ackOpen} onClose={() => setAckOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Acknowledge {po.poNumber}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>Confirm you have received this order. You can add a note, for example a delivery date.</Typography>
          <TextField label="Note (optional)" fullWidth multiline minRows={2} value={note} inputProps={{ maxLength: 500 }}
            onChange={e => setNote(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAckOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={busy} onClick={acknowledge}>Acknowledge</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupplierPos;
// ---- end F8 ----
